import { forwardRef, useImperativeHandle, useRef } from 'react';
import { SectionLabel } from '@/components/ui/section-label';
import { Card } from '@/components/ui/card/Card';

import { DropDownList } from '@/components/form/inputs/DropDownList';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { useTranslation } from '@/hooks/useTranslation';
import { Grip } from 'lucide-react';

import { clampingTypes } from '../../data/clampingOptions';

type ClampingTabProps = {
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
};

export const ClampingTab = forwardRef(function ClampingTab(
  { values, onChange }: ClampingTabProps,
  ref
) {
  const input1Ref = useRef<{ validate: () => boolean }>(null);
  const input2Ref = useRef<{ validate: () => boolean }>(null);
  const { t } = useTranslation();
  useImperativeHandle(ref, () => ({
    validate: () => {
      const v1 = input1Ref.current?.validate() ?? true;
      const v2 = input2Ref.current?.validate() ?? true;

      return v1 && v2;
    },
  }));
  return (
    <div className="grid grid-cols-1 gap-5">
      <Card padding="sm">
        <div className="flex flex-col gap-2">
          <div className="space-y-1">
            <SectionLabel icon={Grip}>{t('traceability.clampingTab.title')}</SectionLabel>
            <p className="text-sm text-muted-foreground">
              {t('traceability.clampingTab.description')}
            </p>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <DropDownList
              ref={input1Ref}
              name="clampingType"
              label={t('traceability.clampingTab.typeLabel')}
              value={(values['clampingType'] as string) ?? ''}
              onChange={(val) => onChange('clampingType', val)}
              options={clampingTypes.map(({ value, labelKey }) => ({
                value,
                label: t(labelKey),
              }))}
              side="bottom"
            />
            <SimpleInput
              ref={input2Ref}
              name="clampingBrand"
              label={t('traceability.clampingTab.brandLabel')}
              value={(values['clampingBrand'] as string) ?? ''}
              onChange={(val) => onChange('clampingBrand', val)}
              placeholder={t('traceability.clampingTab.brandPlaceholder')}
            />
          </div>
        </div>
      </Card>
    </div>
  );
});
